/**
 * __root.tsx — The root layout of the SPA.
 *
 * Responsibilities:
 *   - Pre-fetches the session via `loader` so downstream routes can decide
 *     whether to redirect on the same render pass.
 *   - Reads the session from the QueryClient cache and threads the principal
 *     into <AppShell> (which renders Sidebar / Drawer / Header / Main).
 *   - Wraps the tree in <QueryClientProvider> and <Outlet>. (ScrollRestoration
 *     was removed in TanStack Router ≥ 1.50; main.tsx disables the built-in
 *     `scrollRestoration` option. Hash-anchor scroll is hand-rolled below.)
 *
 * N-19 (sidebar migration) replaces the previous AppHeader + MobileNav pair
 * with the new AppShell wrapper that conditionally renders a left sidebar
 * for authed contexts and a top nav for public contexts.
 */

import { useEffect } from 'react';
import type { ReactNode } from 'react';
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
  useLocation,
} from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppShell } from '../components/shell/AppShell.js';
import { getPortalContext, type ShellUser } from '../lib/navigation.js';
import { sessionQuery, type SessionPrincipal } from '../lib/api/queries.js';
import '../styles/global.css';

export interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // Force a fresh session fetch on every navigation so route guards always
  // see the latest server state. Without this, `prefetchQuery` would return
  // the cached principal (if any) within staleTime, and guards would make
  // redirect decisions based on stale data — the user would see a brief
  // flash to /login before the SPA recovered.
  loader: async ({ context }) => {
    await context.queryClient.fetchQuery({
      ...sessionQuery,
      staleTime: 0,
      gcTime: 5 * 60_000,
    });
    return null;
  },
  component: RootComponent,
});

function mapCachedSessionToShellUser(cached: unknown): ShellUser | null {
  if (!cached || typeof cached !== 'object') return null;
  const value = cached as {
    ok?: boolean;
    data?: { user?: SessionPrincipal };
  };
  if (!value.ok || !value.data?.user) return null;
  const u = value.data.user;
  return {
    userId: u.userId,
    email: u.email,
    name: u.name,
    role: u.role,
    kind: u.kind,
    isApprovedTutor: u.isApprovedTutor,
  };
}

function RootComponent(): ReactNode {
  const { queryClient } = Route.useRouteContext();
  const location = useLocation();
  const pathname = location.pathname;

  const cachedSession = queryClient.getQueryData(sessionQuery.queryKey);
  const user = mapCachedSessionToShellUser(cachedSession);
  const context = getPortalContext(user);

  // Handle hash-anchor scroll on SPA navigation (e.g. sidebar Siswa link
  // `/dashboard#students`). <ScrollRestoration> from TanStack Router handles
  // back/forward but does not auto-scroll to #hash on forward nav, so we
  // do it ourselves whenever pathname or hash changes.
  useEffect(() => {
    if (!location.hash) return;
    const id = decodeURIComponent(location.hash.slice(1));
    if (!id) return;
    // Defer until after the new route has rendered its DOM.
    const timer = window.setTimeout(() => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 60);
    return () => window.clearTimeout(timer);
  }, [pathname, location.hash]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppShell context={context} pathname={pathname} user={user}>
        <Outlet />
      </AppShell>
    </QueryClientProvider>
  );
}

/**
 * Build the singleton QueryClient. Defaults are tuned for an auth-heavy
 * SPA: short staleTime, single retry, no refocus refetching.
 */
export function makeQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

/**
 * Re-export `redirect` so downstream route `beforeLoad` handlers can
 * `import { redirect } from '~root-or-app'`.
 */
export { redirect };
